import React, {useState} from 'react';
import {Stack, Box, Text, Group, Button, Anchor, Textarea, Paper, Badge, Divider} from '@mantine/core';
import {useAvatarImage} from '@/hooks/useAvatarImage';
import {Avatar} from '@/components/Avatar';
import {AvatarUpload} from '@/components/AvatarUpload';
import {useModule} from '@/hooks/useModule';
import {useAction, useSelectorWithRefresh} from '@/store/kysely_module';
import {useMutation} from '@tanstack/react-query';
import {notifications} from '@mantine/notifications';
import {useCurrentUser} from '../../hooks/useCurrentUser';
import {IconMusic} from '@tabler/icons-react';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {formatDuration, formatTimeAgo} from '@/components/new_dashboard/utils/timeFormatting';
import {isAudioFile} from '@/utils/file_utils';
import {useNavigate} from 'react-router-dom';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {SocialLink} from '@/types/social_links';
import {SocialLinksEditor} from '../profile_sidebar/SocialLinksEditor';
import {stringifySocialLinks, extractUsername, getPlatformIcon, buildSocialUrl} from '@/utils/social_links_helpers';

type ProfileMobileViewProps = {
    userId: string;
};


export const ProfileMobileView = ({userId}: ProfileMobileViewProps) => {
    const currentUser = useCurrentUser();
    const isOwnProfile = currentUser?.id === userId;
    const navigate = useNavigate();

    const filesModule = useModule('MSFiles');
    const userModule = useModule('User');
    const addFileToWorkspaceAction = useAction('addFileToWorkspace');
    const updateObjectAttributeAction = useAction('updateObjectAttribute');

    const [profileData, refreshProfile] = useSelectorWithRefresh('getUserProfileData', {user_id: userId}, null, {workspace_id: userId});
    const [authenticatedData] = useSelectorWithRefresh('getUserProfileDataAuthenticated', {user_id: userId}, null, {workspace_id: 'public'});
    const {data: profilePictureUrl, refetch: refetchAvatar} = useAvatarImage(userId, profileData?.user?.profile_picture_file_id || null, profileData?.user?.updated_at || null);

    const [isEditingAvatar, setIsEditingAvatar] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [isEditingSocial, setIsEditingSocial] = useState(false);

    const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
    const [newUploadedBlobUrl, setNewUploadedBlobUrl] = useState<string | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const [bioEdit, setBioEdit] = useState(profileData?.bio || '');
    const [socialLinksEdit, setSocialLinksEdit] = useState<SocialLink[]>(profileData?.socialLinks || []);

    const currentAvatarSrc = newUploadedBlobUrl || profilePictureUrl;

    const handleChooseImage = async () => {
        if (!currentUser) return;
        setIsUploadingImage(true);
        try {
            const result = await filesModule.client().chooseFileAndUpload(currentUser.id, {
                shouldUseDirectory: false,
                onUploadStart: (fileName) => {
                    notifications.show({
                        id: 'profile-image-upload',
                        loading: true,
                        title: 'Uploading profile image',
                        message: fileName,
                        autoClose: false,
                    });
                },
                onIsDirectory: () => {},
            });

            notifications.hide('profile-image-upload');

            if (result) {
                const fileInfo = {
                    name: result.name,
                    fullPath: result.fullPath,
                    mimeType: result.mimeType,
                    size: result.size,
                    workspace_id: currentUser.id,
                    hash: result.hash,
                    lastModifiedAt: result.lastModifiedAt || Date.now(),
                };

                const workspaceFile = await addFileToWorkspaceAction(fileInfo, {workspace_id: currentUser.id});
                if (workspaceFile) {
                    setUploadedFileId(workspaceFile.id);
                    const src = await filesModule.client().getFileSrc(workspaceFile);
                    setNewUploadedBlobUrl(src);
                }
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            notifications.show({
                title: 'Error uploading image',
                message: 'Please try again',
                color: 'red',
            });
        } finally {
            setIsUploadingImage(false);
        }
    };

    const saveAvatarMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser || !uploadedFileId) return;
            await userModule.updateProfile({profile_picture_file_id: uploadedFileId});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                refreshProfile();
                notifications.show({
                    title: 'Avatar updated',
                    message: 'Your profile picture has been updated',
                    color: 'green',
                });
                setIsEditingAvatar(false);
                setUploadedFileId(null);
                setNewUploadedBlobUrl(null);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating avatar',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    const saveBioMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser) return;
            await updateObjectAttributeAction({
                parent_id: currentUser.id,
                parent_type: 'User',
                attribute: 'bio',
                value: bioEdit.trim() || null,
            }, {workspace_id: currentUser.id});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                refreshProfile();
                notifications.show({
                    title: 'Bio updated',
                    message: 'Your bio has been updated',
                    color: 'green',
                });
                setIsEditingBio(false);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating bio',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    const saveSocialMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser) return;

            const linksWithFullUrls = socialLinksEdit.map(link => ({
                ...link,
                url: buildSocialUrl(link.url, link.type)
            }));

            await updateObjectAttributeAction({
                parent_id: currentUser.id,
                parent_type: 'User',
                attribute: 'social_links',
                value: linksWithFullUrls.length > 0 ? stringifySocialLinks(linksWithFullUrls) : null,
            }, {workspace_id: currentUser.id});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                refreshProfile();
                notifications.show({
                    title: 'Social links updated',
                    message: '',
                    color: 'green',
                });
                setIsEditingSocial(false);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating social links',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    if (!profileData?.user) {
        return <Text p="md">Loading...</Text>;
    }

    const {user, bio, socialLinks} = profileData;

    return (
        <Stack gap="lg" p="md" pb="xl">
            <Box style={{textAlign: 'center'}}>
                {isEditingAvatar ? (
                    <Box>
                        <AvatarUpload
                            userName={user.name}
                            currentImageUrl={currentAvatarSrc}
                            size="xl"
                            onImageSelect={handleChooseImage}
                            isUploading={isUploadingImage}
                            disabled={saveAvatarMutation.isPending}
                        />
                        <Group justify="center" gap="xs" mt="md">
                            <Button
                                size="xs"
                                onClick={() => saveAvatarMutation.mutate()}
                                loading={saveAvatarMutation.isPending}
                                disabled={!uploadedFileId}
                            >
                                Save
                            </Button>
                            <Button
                                size="xs"
                                variant="default"
                                onClick={() => {
                                    setIsEditingAvatar(false);
                                    setUploadedFileId(null);
                                    setNewUploadedBlobUrl(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </Group>
                    </Box>
                ) : (
                    <Box style={{position: 'relative', display: 'inline-block'}}>
                        <Avatar
                            src={profilePictureUrl}
                            userName={user.name}
                            size="xl"
                            onImageError={() => refetchAvatar()}
                        />
                        {isOwnProfile && (
                            <Anchor
                                size="xs"
                                onClick={() => setIsEditingAvatar(true)}
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    backgroundColor: 'var(--mantine-color-body)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid var(--mantine-color-default-border)',
                                }}
                            >
                                Edit
                            </Anchor>
                        )}
                    </Box>
                )}
                <Text fw={600} size="lg" mt="md">{user.name}</Text>
                <Text c="dimmed" size="sm">@{user.name.toLowerCase().replace(/\s+/g, '_')}</Text>
            </Box>

            {profileData?.publicFiles && profileData.publicFiles.length > 0 && (
                <>
                    <Divider />
                    <Box>
                        <Group gap="xs" mb="sm">
                            <IconMusic size={16} />
                            <Text size="sm" fw={500}>Public Tracks</Text>
                            <Badge size="xs" variant="light">{profileData.publicFiles.length}</Badge>
                        </Group>
                        <Stack gap="xs">
                            {profileData.publicFiles.slice(0, 5).map((file) => {
                                const duration = file.attributes?.duration;
                                return (
                                    <Group key={file.id} gap="xs" wrap="nowrap">
                                        {isAudioFile(file.name) && <PlayIcon fileInfo={file} size={14} />}
                                        <Text size="sm" style={{flex: 1}} lineClamp={1}>
                                            {file.name}
                                        </Text>
                                        {duration && (
                                            <Text size="sm" c="dimmed">
                                                {formatDuration(Number(duration))}
                                            </Text>
                                        )}
                                    </Group>
                                );
                            })}
                            {profileData.publicFiles.length > 5 && (
                                <Text size="xs" c="dimmed">
                                    +{profileData.publicFiles.length - 5} more tracks
                                </Text>
                            )}
                        </Stack>
                    </Box>
                </>
            )}

            {authenticatedData?.recentProjectRevisions && authenticatedData.recentProjectRevisions.length > 0 && (
                <>
                    <Divider />
                    <Box>
                        <Text size="sm" fw={500} mb="xs">Recent Activity</Text>
                        <Text size="xs" c="dimmed" mb="sm">
                            Activity from workspaces you both belong to
                        </Text>
                        <Stack gap="xs">
                            {authenticatedData.recentProjectRevisions.slice(0, 5).map((revision) => {
                                const workspace = authenticatedData.mutualWorkspaces.find(w => w.id === revision.workspace_id);
                                return (
                                    <Paper
                                        key={revision.id}
                                        withBorder
                                        p="xs"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => {
                                            navigateToPathForReason(
                                                navigate,
                                                'navigate_to_project_page_from_project_view_page',
                                                `/workspaces/${revision.workspace_id}/projects/${revision.project_id}`
                                            );
                                        }}
                                    >
                                        <Stack gap={4}>
                                            <Text size="sm" fw={500} lineClamp={1}>
                                                {revision.project_name}
                                            </Text>
                                            <Group gap="xs">
                                                <Badge size="xs" variant="light">
                                                    {workspace?.name || 'Unknown'}
                                                </Badge>
                                                <Text size="xs" c="dimmed">
                                                    {formatTimeAgo(revision.created_at)}
                                                </Text>
                                            </Group>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                            {authenticatedData.recentProjectRevisions.length > 5 && (
                                <Text size="xs" c="dimmed">
                                    +{authenticatedData.recentProjectRevisions.length - 5} more activities
                                </Text>
                            )}
                        </Stack>
                    </Box>
                </>
            )}

            <Box>
                {isOwnProfile && !bio && !isEditingBio && (
                    <Anchor size="sm" onClick={() => setIsEditingBio(true)}>
                        Add bio
                    </Anchor>
                )}
                {isEditingBio ? (
                    <Box>
                        <Text size="sm" mb="xs" fw={500}>Bio</Text>
                        <Textarea
                            size='sm'
                            placeholder="Tell us about yourself..."
                            value={bioEdit}
                            onChange={e => setBioEdit(e.target.value)}
                            rows={3}
                            maxLength={500}
                        />
                        <Text size="xs" c="dimmed" mt={4} mb="xs">
                            {bioEdit.length}/500 characters
                        </Text>
                        <Group gap="xs">
                            <Button
                                size="xs"
                                onClick={() => saveBioMutation.mutate()}
                                loading={saveBioMutation.isPending}
                            >
                                Save
                            </Button>
                            <Button
                                size="xs"
                                variant="default"
                                onClick={() => {
                                    setIsEditingBio(false);
                                    setBioEdit(bio || '');
                                }}
                            >
                                Cancel
                            </Button>
                        </Group>
                    </Box>
                ) : (
                    bio && (
                        <Paper withBorder p="md" style={{position: 'relative'}}>
                            {isOwnProfile && (
                                <Anchor
                                    size="xs"
                                    onClick={() => setIsEditingBio(true)}
                                    style={{position: 'absolute', top: 8, right: 8}}
                                >
                                    Edit
                                </Anchor>
                            )}
                            <Text size="sm" fw={500} mb="xs">Bio</Text>
                            <Text size="sm" style={{whiteSpace: 'pre-wrap'}}>{bio}</Text>
                        </Paper>
                    )
                )}
            </Box>

            <Box>
                {isOwnProfile && (!socialLinks || socialLinks.length === 0) && !isEditingSocial && (
                    <Anchor size="sm" onClick={() => setIsEditingSocial(true)}>
                        Add social links
                    </Anchor>
                )}
                {isEditingSocial ? (
                    <Box>
                        <Text size="sm" mb="xs" fw={500}>Social Links</Text>
                        <SocialLinksEditor
                            links={socialLinksEdit}
                            onChange={setSocialLinksEdit}
                        />
                        <Group gap="xs" mt="xs">
                            <Button
                                size="xs"
                                onClick={() => saveSocialMutation.mutate()}
                                loading={saveSocialMutation.isPending}
                            >
                                Save
                            </Button>
                            <Button
                                size="xs"
                                variant="default"
                                onClick={() => {
                                    setIsEditingSocial(false);
                                    setSocialLinksEdit(socialLinks || []);
                                }}
                            >
                                Cancel
                            </Button>
                        </Group>
                    </Box>
                ) : (
                    socialLinks && socialLinks.length > 0 && (
                        <Paper withBorder p="md" style={{position: 'relative'}}>
                            {isOwnProfile && (
                                <Anchor
                                    size="xs"
                                    onClick={() => setIsEditingSocial(true)}
                                    style={{position: 'absolute', top: 8, right: 8}}
                                >
                                    Edit
                                </Anchor>
                            )}
                            <Text size="sm" fw={500} mb="xs">Social Links</Text>
                            <Stack gap="xs">
                                {socialLinks.map((link: SocialLink) => {
                                    const Icon = getPlatformIcon(link.type);
                                    const fullUrl = buildSocialUrl(link.url, link.type);
                                    const displayText = extractUsername(fullUrl, link.type);

                                    return (
                                        <Anchor key={link.id} href={fullUrl} target="_blank" size="sm">
                                            <Group gap="xs">
                                                <Icon size={16} />
                                                <Text>{displayText}</Text>
                                            </Group>
                                        </Anchor>
                                    );
                                })}
                            </Stack>
                        </Paper>
                    )
                )}
            </Box>
        </Stack>
    );
};
